function parseNumber(value) {
  const parsed = Number(value)
  return !isNaN(parsed) ? parsed : undefined
}

/**
 * <whatsapp-float-button> — Web Component
 *
 * Atributos:
 *   corner   : "tl" | "tr" | "bl" | "br"  (padrão: "br")
 *   gap-h    : número em px                 (padrão: 24)
 *   gap-v    : número em px                 (padrão: 24)
 *   wa-phone : numero destino do WhatsApp (somente digitos)
 *   wa-message : mensagem padrao opcional
 *
 * Uso:
 *   <whatsapp-float-button corner="br" gap-h="28" gap-v="28" wa-phone="5511999999999">
 *     <button type="button" class="whatsapp-button">...</button>
 *     <span part="tooltip" role="tooltip">Mensagem</span>
 *   </whatsapp-float-button>
 */

class WhatsappFloatButton extends HTMLElement {
  get attr() {
    return {
      corner: this.getAttribute('corner') || 'br',
      gap: {
        horizontal: parseNumber(this.getAttribute('gap-h')) || 28,
        vertical: parseNumber(this.getAttribute('gap-v')) || 28,
      },
      cornerKey: this.getAttribute('corner-key') || 'wa_float_corner',
      whatsapp: {
        phoneNumber: (this.getAttribute('wa-phone') || '').replace(/\D/g, ''),
        message: this.getAttribute('wa-message') || '',
      },
      antiBot: {
        honeypotFieldId: this.getAttribute('hp-field-id') || 'wa_website',
        minDelayMs: parseNumber(this.getAttribute('min-delay-ms')) || 1500,
        rateLimit: {
          max: parseNumber(this.getAttribute('rate-max')) || 3,
          windowMin: parseNumber(this.getAttribute('rate-window-min')) || 10,
          storageKey: this.getAttribute('rate-key') || 'wa_float_rl',
        },
      },
    }
  }

  constructor() {
    super()
    this._isDragging = false
    this._corner = 'br'
    this._pageLoadStartedAt = Date.now()
    this._antiBotClickHandler = null
  }

  connectedCallback() {
    this._corner = this._loadCorner()

    this._setCorner(this._corner, false)
    this._bindEvents()
    this._bindAntiBotClickGuard()
  }

  disconnectedCallback() {
    this.onmousedown = null
    this.onclick = null
    this.ondragstart = null
    this.ontouchstart = null

    if (window.visualViewport) {
      window.visualViewport.removeEventListener(
        'resize',
        this._onViewportResize
      )
      window.visualViewport.removeEventListener(
        'scroll',
        this._onViewportResize
      )
    } else {
      window.removeEventListener('resize', this._onViewportResize)
    }

    this._unbindAntiBotClickGuard()
  }

  /* ── Utils ──────────────────────────────────────── */

  _viewport() {
    if (window.visualViewport) {
      return {
        width: window.visualViewport.width,
        height: window.visualViewport.height,
      }
    }

    return {
      width: document.documentElement.clientWidth,
      height: document.documentElement.clientHeight,
    }
  }

  _variables() {
    return {
      view: this._viewport(),
      offset: {
        x: this.offsetLeft,
        y: this.offsetTop,
      },
      safety: this.offsetWidth || 56,
      threshold: 5,
    }
  }

  /* ── Posicionamento ─────────────────────────────── */

  _positions() {
    const { view, safety } = this._variables()
    const top = view.height - safety - this.attr.gap.vertical
    const left = view.width - safety - this.attr.gap.horizontal

    return {
      tl: { top: this.attr.gap.vertical, left: this.attr.gap.horizontal },
      tr: { top: this.attr.gap.vertical, left: left },
      bl: { top: top, left: this.attr.gap.horizontal },
      br: { top: top, left: left },
    }
  }

  _changeCornerClasses(corner = 'br') {
    this._corner = corner
    this.classList.remove('tl', 'tr', 'bl', 'br')
    this.classList.add(corner)
    this._saveCorner(corner)
  }

  _setCornerPosition(corner = 'br') {
    const pos = this._positions()[corner]

    this.style.top = `${pos.top}px`
    this.style.left = `${pos.left}px`
  }

  _setCorner(corner, animate) {
    if (!animate) {
      this.style.transition = 'none'
      this._setCornerPosition(corner)
      requestAnimationFrame(() => (this.style.transition = ''))
    } else {
      this._setCornerPosition(corner)
    }

    this._changeCornerClasses(corner)
  }

  _cornerFromPos() {
    const { view, safety } = this._variables()
    const offset = {
      x: this.offsetLeft,
      y: this.offsetTop,
    }

    const horizontal = offset.x + safety / 2 > view.width / 2
    const vertical = offset.y + safety / 2 > view.height / 2

    if (!horizontal && !vertical) return 'tl'
    if (horizontal && !vertical) return 'tr'
    if (!horizontal && vertical) return 'bl'

    return 'br'
  }

  _onMoveItem({ start, move, shift }) {
    const { view, safety, threshold } = this._variables()

    const movedX = Math.abs(move.x - start.x)
    const movedY = Math.abs(move.y - start.y)

    if (movedX < threshold && movedY < threshold) return

    const posLimiter = (value, max) => Math.max(0, Math.min(value, max))

    this._isDragging = true
    this.classList.add('is-dragging')

    this.style.left = `${posLimiter(move.x - shift.x, view.width - safety)}px`
    this.style.top = `${posLimiter(move.y - shift.y, view.height - safety)}px`
  }

  _setItemOnCorner() {
    if (this._isDragging) {
      const corner = this._cornerFromPos()

      this.classList.remove('is-dragging')
      this._setCorner(corner, true)
    }
  }

  /* ── Eventos ────────────────────────────────────── */

  _bindEvents() {
    // ── Mouse ──
    this.onmousedown = (event) => {
      this._isDragging = false

      const start = {
        x: event.clientX,
        y: event.clientY,
      }

      const shift = {
        x: start.x - this.offsetLeft,
        y: start.y - this.offsetTop,
      }

      const onMouseMove = (eventMove) => {
        const move = {
          x: eventMove.clientX,
          y: eventMove.clientY,
        }

        this._onMoveItem({ start, move, shift })
      }

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
        this._setItemOnCorner()
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    }

    this.onclick = (e) => {
      if (this._isDragging) {
        e.preventDefault()
        this._isDragging = false
      }
    }

    this.ondragstart = () => false

    // ── Touch ──
    this.ontouchstart = (event) => {
      this._isDragging = false

      const touch = event.touches[0]
      const start = {
        x: touch.clientX,
        y: touch.clientY,
      }
      const shift = {
        x: start.x - this.offsetLeft,
        y: start.y - this.offsetTop,
      }

      const onTouchMove = (eventMove) => {
        eventMove.preventDefault()
        const t = eventMove.touches[0]

        const move = {
          x: t.clientX,
          y: t.clientY,
        }

        this._onMoveItem({ start, move, shift })
      }

      const onTouchEnd = () => {
        this.removeEventListener('touchmove', onTouchMove)
        this.removeEventListener('touchend', onTouchEnd)
        this._setItemOnCorner()
      }

      this.addEventListener('touchmove', onTouchMove, { passive: false })
      this.addEventListener('touchend', onTouchEnd)
    }

    this._onViewportResize = () => this._setCorner(this._corner, false)

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this._onViewportResize)
      window.visualViewport.addEventListener('scroll', this._onViewportResize)
    } else {
      window.addEventListener('resize', this._onViewportResize)
    }
  }

  /* ── Anti-bot (client-side) ─────────────────────── */

  _bindAntiBotClickGuard() {
    const whatsappButton = this.querySelector('.whatsapp-button')

    if (!whatsappButton) return

    this._antiBotClickHandler = (event) => {
      event.preventDefault()

      if (this._isDragging) {
        event.preventDefault()
        return
      }

      const {
        antiBot: { rateLimit },
        whatsapp: { phoneNumber, message },
      } = this.attr

      if (this._isHoneypotFilled() || this._isBeforeMinInteractionTime()) return

      if (this._isRateLimited(rateLimit.storageKey, rateLimit.max)) {
        whatsappButton.setAttribute('aria-disabled', 'true')
        setTimeout(() => whatsappButton.removeAttribute('aria-disabled'), 3000)
        return
      }

      this._registerRateHit(rateLimit.storageKey, rateLimit.windowMin)

      const whatsappUrl = this._buildWhatsAppUrl(phoneNumber, message)

      if (!whatsappUrl) return

      window.open(whatsappUrl, '_blank', 'noopener,noreferrer')
    }

    whatsappButton.addEventListener('click', this._antiBotClickHandler)
  }

  _unbindAntiBotClickGuard() {
    const whatsappButton = this.querySelector('.whatsapp-button')
    if (!whatsappButton || !this._antiBotClickHandler) return

    whatsappButton.removeEventListener('click', this._antiBotClickHandler)
    this._antiBotClickHandler = null
  }

  _buildWhatsAppUrl(phoneNumber, message) {
    const sanitizedPhone = String(phoneNumber || '').replace(/\D/g, '')

    if (!sanitizedPhone) return ''

    const baseUrl = `https://wa.me/${sanitizedPhone}`
    const normalizedMessage = String(message || '').trim()

    if (!normalizedMessage) return baseUrl

    return `${baseUrl}?text=${encodeURIComponent(normalizedMessage)}`
  }

  _isHoneypotFilled() {
    const honeypotFieldId = this.attr.antiBot.honeypotFieldId
    const honeypotInput =
      this.querySelector(`#${honeypotFieldId}`) ||
      document.getElementById(honeypotFieldId)

    return Boolean(honeypotInput && honeypotInput.value !== '')
  }

  _isBeforeMinInteractionTime() {
    const { minDelayMs } = this.attr.antiBot
    const timeSinceLoad = Date.now() - this._pageLoadStartedAt

    return timeSinceLoad < minDelayMs
  }

  _isRateLimited(storageKey, maxHits) {
    try {
      const rawState = localStorage.getItem(storageKey)
      if (!rawState) return false

      const parsedState = JSON.parse(rawState)
      const nowSeconds = Math.floor(Date.now() / 1000)

      if (nowSeconds >= parsedState.exp) {
        localStorage.removeItem(storageKey)
        return false
      }

      return parsedState.count >= maxHits
    } catch (error) {
      return false
    }
  }

  _registerRateHit(storageKey, windowMinutes) {
    try {
      const rawState = localStorage.getItem(storageKey)
      const nowSeconds = Math.floor(Date.now() / 1000)
      const expirationSeconds = nowSeconds + windowMinutes * 60

      if (!rawState) {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            count: 1,
            exp: expirationSeconds,
          })
        )
        return
      }

      const parsedState = JSON.parse(rawState)

      if (nowSeconds >= parsedState.exp) {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            count: 1,
            exp: expirationSeconds,
          })
        )
        return
      }

      localStorage.setItem(
        storageKey,
        JSON.stringify({
          count: parsedState.count + 1,
          exp: parsedState.exp,
        })
      )
    } catch (error) {}
  }

  /* ── Persistence ────────────────────────────────── */

  _saveCorner(corner) {
    try {
      localStorage.setItem(this.attr.cornerKey, corner)
    } catch (e) {}
  }

  _loadCorner() {
    const corner = this.attr.corner || 'br'

    try {
      return localStorage.getItem(this.attr.cornerKey) || corner
    } catch (e) {
      return corner
    }
  }
}

if (!window.customElements.get('whatsapp-float-button')) {
  window.customElements.define('whatsapp-float-button', WhatsappFloatButton)
}
